/**
 * AcceptInvitationPage — manager one-time invite link landing page.
 *
 * Flow:
 *   1. On mount: read ?token= and ?uid= or ?uuid= (alias) from URL → POST /auth/login/invite/redeem
 *      → backend validates token, starts CUSTOM_AUTH, emails OTP to manager's address,
 *        returns { session, email }
 *   2. Manager enters the 6-digit OTP from their inbox
 *   3. On success: tokens stored, redirect to manager onboarding
 *   4. On expired/invalid token or missing link params: friendly message + link to /login
 *
 * @viewport both
 */
// src/features/auth/AcceptInvitationPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Link,
} from '@mui/material';
import {
  MarkEmailRead as InboxIcon,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useAuth } from '@/core/contexts/AuthContext';
import AuthLayout from '@/features/auth/components/AuthLayout';
import HFRLogo from '@/components/common/HFRLogo';
import authService from '@/services/authService';
import { TOUCH, FONT_SIZE } from '@/core/platform/platformStandards';

const OTP_LENGTH = 6;

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithMagicLink } = useAuth();
  const codeInputRef = useRef(null);

  const token = searchParams.get('token') || '';
  const userId = searchParams.get('uid') || searchParams.get('uuid') || '';

  // ── Redemption state ──────────────────────────────────────────────────────
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  // Once redeemed: { session, email }
  const [redeemed, setRedeemed] = useState(null);

  // ── OTP state ─────────────────────────────────────────────────────────────
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  // NOTE: No isAuthenticated redirect here — this route must work regardless of
  // whether another user (e.g. an admin testing the link) is currently logged in.
  // The OTP verification will authenticate as the invited manager.

  // Auto-redeem on mount
  useEffect(() => {
    if (!token || !userId) {
      setRedeemError(
        'This link is incomplete or outdated. Approved managers sign in using the email address '
          + 'on file: open the sign-in page, enter your email, and use the verification code sent to your inbox.'
      );
      return;
    }
    let cancelled = false;

    const redeem = async () => {
      setRedeeming(true);
      setRedeemError('');
      try {
        const result = await authService.redeemInvite(token, userId);
        if (!cancelled) {
          setRedeemed({ session: result.session, email: result.email });
          // Auto-focus OTP field
          setTimeout(() => codeInputRef.current?.focus(), 100);
        }
      } catch (err) {
        if (!cancelled) {
          const serverMsg = err?.response?.data?.message || '';
          setRedeemError(
            serverMsg ||
              'This invitation link has expired or has already been used. ' +
              'You can log in using your email address on the sign-in page.'
          );
        }
      } finally {
        if (!cancelled) setRedeeming(false);
      }
    };

    redeem();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── OTP handlers ──────────────────────────────────────────────────────────

  const handleCodeChange = useCallback((e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH);
    setCode(val);
    setVerifyError('');
  }, []);

  const handleVerify = useCallback(async () => {
    if (code.length !== OTP_LENGTH) {
      setVerifyError(`Enter the ${OTP_LENGTH}-digit code from your email.`);
      return;
    }
    if (!redeemed?.session || !redeemed?.email) return;

    setVerifying(true);
    setVerifyError('');
    try {
      await loginWithMagicLink(redeemed.email, redeemed.session, code);
      // DashboardContainer checks onboarding flags on entry; a manager with
      // has_firm_profile: false will be routed to the first-login onboarding flow.
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || '';
      setVerifyError(
        msg.toLowerCase().includes('incorrect') || msg.toLowerCase().includes('mismatch')
          ? 'Incorrect code. Please check your email and try again.'
          : msg || 'Sign-in failed. Please try again.'
      );
    } finally {
      setVerifying(false);
    }
  }, [code, redeemed, loginWithMagicLink, navigate]);

  const handleKeyDown = useCallback(
    (e) => { if (e.key === 'Enter') handleVerify(); },
    [handleVerify]
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AuthLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <HFRLogo />

        {/* Loading */}
        {redeeming && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Validating your invitation…
            </Typography>
          </Box>
        )}

        {/* Invalid / expired token */}
        {!redeeming && redeemError && (
          <>
            <Alert severity="warning" sx={{ mt: 1 }}>
              {redeemError}
            </Alert>
            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              startIcon={<LoginIcon />}
              sx={{ alignSelf: 'flex-start', minHeight: TOUCH.minSize }}
            >
              Go to sign-in page
            </Button>
          </>
        )}

        {/* OTP entry */}
        {!redeeming && redeemed && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
              <InboxIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Check your inbox
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              We&apos;ve sent a sign-in code to{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {redeemed.email}
              </Box>
              . Enter it below to access your account.
            </Typography>

            <TextField
              inputRef={codeInputRef}
              label="Sign-in code"
              value={code}
              onChange={handleCodeChange}
              onKeyDown={handleKeyDown}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              error={!!verifyError}
              helperText={verifyError}
              fullWidth
              size="small"
              disabled={verifying}
              inputProps={{
                maxLength: OTP_LENGTH,
                'aria-label': 'One-time sign-in code',
                style: {
                  fontSize: FONT_SIZE.xl,
                  letterSpacing: '0.3em',
                  textAlign: 'center',
                },
              }}
            />

            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={verifying || code.length !== OTP_LENGTH}
              startIcon={verifying ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ minHeight: TOUCH.minSize }}
              fullWidth
            >
              {verifying ? 'Signing in…' : 'Verify code'}
            </Button>

            {/* Spam notice — critical for first-time email recipients */}
            <Alert severity="info" icon={false} sx={{ fontSize: FONT_SIZE.xs }}>
              <strong>Can&apos;t find the email?</strong> Check your <strong>junk or spam
              folder</strong>. If it&apos;s there, mark it as &quot;Not Spam&quot; or move it to your
              inbox — this ensures future sign-in codes reach you reliably.
            </Alert>

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              Need help?{' '}
              <Link component={RouterLink} to="/login" underline="hover">
                Go to sign-in page
              </Link>
            </Typography>
          </>
        )}
      </Box>
    </AuthLayout>
  );
}
